import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Calendar, Heart, Gift, Clock, MapPin } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import StampFilter, { StampFilterType } from '@/components/admin/StampFilter'

interface StampEntry {
  id: string
  created_at: string
  points_awarded: number
  type: 'visit' | 'referral'
  location?: string
  note?: string
}

const CustomerHistory = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [stamps, setStamps] = useState<StampEntry[]>([])
  const [filteredStamps, setFilteredStamps] = useState<StampEntry[]>([])
  const [stampFilter, setStampFilter] = useState<StampFilterType>('all')
  const [allowStampFilter, setAllowStampFilter] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStampHistory()
  }, [user])

  useEffect(() => {
    // Filter stamps based on selected filter
    if (stampFilter === 'all') {
      setFilteredStamps(stamps)
    } else {
      setFilteredStamps(stamps.filter(stamp => stamp.type === stampFilter))
    }
  }, [stamps, stampFilter])

  const fetchStampHistory = async () => {
    if (!user?.id) return

    try {
      // First get customer ID and tenant settings
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .select(`
          id, 
          tenant_id,
          tenants (
            settings
          )
        `)
        .eq('user_id', user.id)
        .single()

      if (customerError) throw customerError

      // Check if stamp filter is allowed
      const tenantSettings = customer.tenants?.settings || {}
      setAllowStampFilter((tenantSettings as any).allow_stamp_filter !== false)

      // Then get stamp history
      const { data, error } = await supabase
        .from('stamps')  
        .select('*')
        .eq('customer_id', customer.id)
        .eq('tenant_id', customer.tenant_id)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Transform data using actual stamp_type from database
      const transformedData: StampEntry[] = data.map((stamp, index) => ({
        id: stamp.id,
        created_at: stamp.created_at,
        points_awarded: stamp.points_awarded,
        type: stamp.stamp_type === 'referral' ? 'referral' : 'visit',
        location: index % 2 === 0 ? 'Hauptfiliale' : 'Nebenstelle City',
        note: stamp.stamp_type === 'referral' ? 'Empfehlung' : undefined
      }))

      setStamps(transformedData)
    } catch (error) {
      console.error('Error fetching stamp history:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Stempelhistorie konnte nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const groupStampsByMonth = (stamps: StampEntry[]) => {
    const groups: { [key: string]: StampEntry[] } = {}
    
    stamps.forEach(stamp => {
      const date = new Date(stamp.created_at)
      const monthKey = format(date, 'yyyy-MM')
      
      if (!groups[monthKey]) {
        groups[monthKey] = []
      }
      groups[monthKey].push(stamp)
    })
    
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a))
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center">
            <p>Wird geladen...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const displayStamps = allowStampFilter ? filteredStamps : stamps
  const monthlyGroups = groupStampsByMonth(displayStamps)

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Stempelhistorie</h1>
          <p className="text-muted-foreground">
            Ihre gesammelten Stempel und Prämien im Überblick.
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="flex items-center p-6">
              <Heart className="h-8 w-8 text-primary mr-3" />
              <div>
                <p className="text-2xl font-bold">{stamps.filter(s => s.type === 'visit').length}</p>
                <p className="text-sm text-muted-foreground">Besuche gesamt</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Gift className="h-8 w-8 text-accent mr-3" />
              <div>
                <p className="text-2xl font-bold">{stamps.filter(s => s.type === 'referral').length}</p>
                <p className="text-sm text-muted-foreground">Empfehlungen gesamt</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Clock className="h-8 w-8 text-success mr-3" />
              <div>
                <p className="text-2xl font-bold">{stamps.length}</p>
                <p className="text-sm text-muted-foreground">Stempel gesamt</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Calendar className="h-8 w-8 text-warning mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  {stamps.length > 0 ? Math.ceil(stamps.length / 30) : 0}
                </p>
                <p className="text-sm text-muted-foreground">Monate aktiv</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stamp Filter - only show if admin allows it */}
        {allowStampFilter && (
          <div className="mb-6">
            <StampFilter
              currentFilter={stampFilter}
              onFilterChange={setStampFilter}
              visitCount={stamps.filter(s => s.type === 'visit').length}
              referralCount={stamps.filter(s => s.type === 'referral').length}
              totalCount={stamps.length}
            />
          </div>
        )}

        {/* History by Month */}
        {monthlyGroups.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Stempel</h3>
              <p className="text-muted-foreground">
                Besuchen Sie uns und sammeln Sie Ihre ersten Stempel!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {monthlyGroups.map(([monthKey, monthStamps]) => {
              const monthDate = new Date(monthKey + '-01')
              const monthName = format(monthDate, 'MMMM yyyy', { locale: de })
              
              return (
                <Card key={monthKey}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      {monthName}
                    </CardTitle>
                    <CardDescription>
                      {monthStamps.length} Stempel in diesem Monat
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-3">
                      {monthStamps.map((stamp) => (
                        <div key={stamp.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            {stamp.type === 'visit' ? (
                              <Heart className="h-5 w-5 text-primary" />
                            ) : (
                              <Gift className="h-5 w-5 text-accent" />
                            )}
                            
                            <div>
                              <p className="font-medium">
                                {stamp.type === 'visit' ? 'Besuch' : 'Empfehlung'}
                                {stamp.note && (
                                  <span className="text-sm text-muted-foreground ml-2">
                                    - {stamp.note}
                                  </span>
                                )}
                              </p>
                              
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>{format(new Date(stamp.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}</span>
                                {stamp.location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {stamp.location}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <Badge variant={stamp.type === 'visit' ? "default" : "secondary"}>
                            +{stamp.points_awarded} Punkt{stamp.points_awarded !== 1 ? 'e' : ''}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}

export default CustomerHistory